import { ReactElement } from "react";

import { useRouter } from "next/router";

import {
  DirectDebitFieldConfig,
  DirectDebitFieldConfigIBAN,
  DirectDebitWithSortCode,
  DirectDebitNoSortCode,
  isDirectDebitSortCode,
  isFullBankRouting,
  DirectDebitType,
} from "@evergiving/common/dist/es2015/form";

import { FormField } from "@/lib/components/FormField";

import { IBANField } from "./IBANField";
import directDebit from "./form-fields-direct-debit.svg";
import { SingleSelectField } from "../SingleSelectField";

const FormFieldsDirectDebitIBAN = ({
  directDebitFieldConfig,
}: {
  directDebitFieldConfig: DirectDebitFieldConfigIBAN;
}) => <IBANField config={directDebitFieldConfig.iban} />;

const ContainerWithDirectDebitLogo = ({
  children,
}: {
  children: ReactElement;
}) => {
  const style = {
    backgroundImage: `url(${String(directDebit)})`,
    backgroundSize: "auto 50px",
    backgroundRepeat: "no-repeat",
    paddingBottom: "50px",
    backgroundPosition: "bottom right",
    marginBottom: "1.5em",
  };

  return <div style={style}>{children}</div>;
};

const FormFieldsDirectDebitNonIBAN = ({
  directDebitFieldConfig,
}: {
  directDebitFieldConfig: DirectDebitWithSortCode | DirectDebitNoSortCode;
}) => {
  const {
    query: { locale },
  } = useRouter();

  const accountNumberField = (
    <FormField
      config={directDebitFieldConfig.accountNumber}
      name="directDebitAccountNumber"
      htmlName="ccnumber"
      autoComplete="cc-number"
    />
  );

  const directDebitFields = isDirectDebitSortCode(directDebitFieldConfig) ? (
    (() => {
      const sortCodeField = (
        <FormField
          config={directDebitFieldConfig.sortCode}
          name="directDebitSortCode"
          htmlName="sortcode"
        />
      );

      return (
        <>
          {locale === "en-GB" ? (
            <ContainerWithDirectDebitLogo>
              {sortCodeField}
            </ContainerWithDirectDebitLogo>
          ) : (
            sortCodeField
          )}
        </>
      );
    })()
  ) : (
    <>
      {isFullBankRouting(directDebitFieldConfig) ? (
        <>
          <SingleSelectField
            config={directDebitFieldConfig.financialInstitution}
            name="directDebitFinancialInstitution"
          />
          <SingleSelectField
            config={directDebitFieldConfig.accountType}
            name="directDebitAccountType"
          />
        </>
      ) : null}
    </>
  );

  return (
    <>
      {accountNumberField}
      {directDebitFields}
    </>
  );
};

export type FormFieldsDirectDebitProps = {
  directDebitFieldConfig: DirectDebitFieldConfig;
};

export const FormFieldsDirectDebit = ({
  directDebitFieldConfig,
}: FormFieldsDirectDebitProps) => (
  <>
    <FormField
      config={directDebitFieldConfig.accountHolder}
      name="directDebitAccountHolder"
      htmlName="ccname"
      autoComplete="cc-name"
    />

    {directDebitFieldConfig.type === DirectDebitType.IBAN ? (
      <FormFieldsDirectDebitIBAN
        directDebitFieldConfig={directDebitFieldConfig}
      />
    ) : (
      <FormFieldsDirectDebitNonIBAN
        directDebitFieldConfig={directDebitFieldConfig}
      />
    )}
  </>
);
